/**
 * SectionBodyEditor — TipTap rich text editor for content section bodies.
 *
 * Supports: bold, italic, headings, lists, blockquote, horizontal rule,
 * inline images (with caption + credit via Figure), YouTube embeds,
 * and links. Reuses the existing Figure extension and file upload pipeline.
 *
 * This is a SEPARATE component so useEditor is called unconditionally
 * at the top level, satisfying React's rules-of-hooks.
 */

import React, { useRef, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import Placeholder from '@tiptap/extension-placeholder';
import { getSectionExtensions } from './sectionEditorExtensions';
import { matchAllowedDomain, allowedDomainLabels } from './embedAllowlist';
import { uploadFile, fileUrl } from '../../api/fileStorage';
import { compressImage } from '../../lib/compressImage';
import styles from './ProjectContentSections.module.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface SectionBodyEditorProps {
  /** Initial TipTap JSON content (stringified) or empty string. */
  initialContent: string;
  /** Placeholder text for empty editor. */
  placeholder: string;
  /** Called on every content change with JSON and plain text. */
  onUpdate: (json: string, plainText: string) => void;
  /** Translation function. */
  t: (key: string) => string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function SectionBodyEditor({
  initialContent,
  placeholder,
  onUpdate,
  t,
}: SectionBodyEditorProps) {
  const imageFileRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      ...getSectionExtensions(),
      Placeholder.configure({ placeholder }),
    ],
    content: initialContent
      ? (() => { try { return JSON.parse(initialContent); } catch { return initialContent; } })()
      : '',
    onUpdate: ({ editor: ed }) => {
      onUpdate(JSON.stringify(ed.getJSON()), ed.getText());
    },
  });

  // ── Image upload handler ──
  const handleImageFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/') || !editor) return;
    try {
      const { blob } = await compressImage(file, {
        maxDimension: 1600,
        quality: 0.82,
        maxBytes: 500_000,
      });
      const slug = `section-img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
      const result = await uploadFile(blob, 'section-images', undefined, slug);
      const url = fileUrl(result.key);
      editor.chain().focus().setFigure({ src: url, caption: '', credit: '' }).run();
    } catch (err) {
      console.error('[SectionEditor] image upload failed:', err);
    }
  }, [editor]);

  // ── YouTube embed handler ──
  const handleInsertYouTube = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('YouTube URL:');
    if (!url?.trim()) return;
    const success = editor.chain().focus().setYouTube({ src: url.trim() }).run();
    if (!success) {
      window.alert('Could not extract a YouTube video from that URL.');
    }
  }, [editor]);

  // ── Iframe embed handler ──
  const handleInsertEmbed = useCallback(() => {
    if (!editor) return;
    const sources = allowedDomainLabels().join(', ');
    const raw = window.prompt(
      `Paste an embed URL or full <iframe> embed code.\nSupported sources: ${sources}`
    );
    if (!raw?.trim()) return;

    // Extract src from <iframe> tag if user pasted the full HTML embed code
    let url = raw.trim();
    const iframeSrcMatch = url.match(/<iframe[^>]+src=["']([^"']+)["']/i);
    if (iframeSrcMatch) {
      url = iframeSrcMatch[1];
    }

    const match = matchAllowedDomain(url);
    if (!match) {
      window.alert(
        `That domain is not on the allowed embed list.\nSupported: ${sources}`
      );
      return;
    }
    editor.chain().focus().setIframeEmbed({ src: url }).run();
  }, [editor]);

  // ── Link handler ──
  const handleInsertLink = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('URL:');
    if (!url?.trim()) return;
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url.trim() })
      .run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className={styles.editorWrapper}>
      {/* ── Toolbar ── */}
      <div className={styles.toolbar}>
        <button
          type="button"
          className={editor.isActive('bold') ? styles.toolbarBtnActive : styles.toolbarBtn}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className={editor.isActive('italic') ? styles.toolbarBtnActive : styles.toolbarBtn}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          <em>I</em>
        </button>

        <span className={styles.toolbarSep} />

        <button
          type="button"
          className={editor.isActive('heading', { level: 2 }) ? styles.toolbarBtnActive : styles.toolbarBtn}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Heading"
        >
          H2
        </button>
        <button
          type="button"
          className={editor.isActive('heading', { level: 3 }) ? styles.toolbarBtnActive : styles.toolbarBtn}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          title="Subheading"
        >
          H3
        </button>

        <span className={styles.toolbarSep} />

        <button
          type="button"
          className={editor.isActive('bulletList') ? styles.toolbarBtnActive : styles.toolbarBtn}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet list"
        >
          •
        </button>
        <button
          type="button"
          className={editor.isActive('orderedList') ? styles.toolbarBtnActive : styles.toolbarBtn}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered list"
        >
          1.
        </button>
        <button
          type="button"
          className={editor.isActive('blockquote') ? styles.toolbarBtnActive : styles.toolbarBtn}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          title="Blockquote"
        >
          "
        </button>

        <span className={styles.toolbarSep} />

        <button
          type="button"
          className={styles.toolbarBtn}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Divider"
        >
          ―
        </button>

        <span className={styles.toolbarSep} />

        {/* Image insert */}
        <button
          type="button"
          className={styles.toolbarBtn}
          onClick={() => imageFileRef.current?.click()}
          title="Insert image"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </button>
        <input
          ref={imageFileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleImageFile(file);
            e.target.value = '';
          }}
        />

        {/* YouTube insert */}
        <button
          type="button"
          className={styles.toolbarBtn}
          onClick={handleInsertYouTube}
          title="Insert YouTube video"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="5 3 19 12 5 21 5 3" />
          </svg>
        </button>

        {/* Embed insert (Our World in Data, Tableau, etc.) */}
        <button
          type="button"
          className={styles.toolbarBtn}
          onClick={handleInsertEmbed}
          title="Embed chart or visualization"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
        </button>

        {/* Link insert */}
        <button
          type="button"
          className={editor.isActive('link') ? styles.toolbarBtnActive : styles.toolbarBtn}
          onClick={handleInsertLink}
          title="Insert link"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </button>
      </div>

      {/* ── Editor body ── */}
      <div className={styles.editorBody}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
